import { Button } from '@cluey/cluey-components';
import first from 'lodash/first';
import { Loader2 } from 'lucide-react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { generatePath, useHistory } from 'react-router';

import { api } from '../../../../../api';
import { Student } from '../../../../../api/types/student';
import type { Subscription, SubscriptionDetails } from '../../../../../api/types/subscription';
import checkIcon from '../../../../../assets/images/icon-outline-checked.svg';
import { ShadowContainer } from '../../../../../components/ShadowContainer';
import type { FormFlowProps } from '../../../../../types/hubInterfaces';
import { openStudentHubWithSso } from '../../../../../util/openStudentHubWithSso';
import { PATH_HUB_HOME, PATH_HUB_STUDENT_PROFILE, PATH_HUB_SUBSCRIPTIONS } from '../../../../../util/pagePath';
import { useSetPageHeader } from '../../../../../v2/app-shell/usePageHeader';

// A subscription is valid if its paid-until date hasn't passed or is null (for bundled subscriptions)
const hasValidSubscription = (subscriptions: SubscriptionDetails[]) =>
  subscriptions.some(
    (subscription) =>
      subscription.subscriptionPaidUntilDate === null ||
      moment(subscription.subscriptionPaidUntilDate).isSameOrAfter(moment(), 'day')
  );

interface Props {
  formFlowProps: FormFlowProps;
}

type HistoryState = {
  newStudent?: Student;
  newSubs?: Subscription;
  fromAddStudentSuccess?: boolean;
};

const AddStudentSuccess = ({ formFlowProps }: Props) => {
  const history = useHistory();
  const newStudent = (history.location.state as HistoryState | undefined)?.newStudent;
  const newSubs = (history.location.state as HistoryState | undefined)?.newSubs;
  console.log(newSubs);
  const { firstName, lastName } = newStudent || {};
  const { subscriptionDiscountDuration, subscriptionPaidUntilDate } = first(newSubs?.subscriptions) || {};
  const [selectingStudentId, setSelectingStudentId] = useState<string | null>(null);
  const { data: subscriptionsData, refetch: refetchSubscriptions } = api.subscription.getAll.useQuery();

  const { jump } = formFlowProps;
  useEffect(() => {
    if (!firstName) {
      jump(0);
    }
  }, [firstName, jump]);

  useSetPageHeader({
    label: 'Students',
    showIcon: false,
    showBackButton: true,
  });

  const onGoToProfileClick = () => {
    if (newStudent) {
      const profilePath = generatePath(PATH_HUB_STUDENT_PROFILE, { studentSfid: newStudent.studentId });
      history.push(profilePath, { fromAddStudentSuccess: true });
    } else {
      history.push(PATH_HUB_HOME);
    }
  };

  const onExploreClueyPlusClick = async () => {
    if (!newStudent || selectingStudentId) {
      return;
    }

    setSelectingStudentId(newStudent.studentId);
    try {
      const { data: latestSubscriptions } = await refetchSubscriptions();
      const studentSubscriptions = (latestSubscriptions ?? subscriptionsData)?.find(
        (sub) => sub.studentId === newStudent.studentId
      );
      if (hasValidSubscription(studentSubscriptions?.subscriptions ?? [])) {
        const opened = await openStudentHubWithSso({
          target: '_self',
          returnTo: `/?student=${newStudent.customerNumber}`,
        });
        // Keep the spinner while `_self` navigation proceeds; clear only on failure.
        if (!opened) {
          setSelectingStudentId(null);
        }
        return;
      }
      setSelectingStudentId(null);
      history.push(PATH_HUB_SUBSCRIPTIONS, { studentId: newStudent.studentId });
    } catch {
      setSelectingStudentId(null);
    }
  };

  return (
    <div className="container pb-5 md:pb-0">
      {selectingStudentId &&
        createPortal(
          <div
            className="fixed inset-0 z-40 flex items-center justify-center bg-background/80 backdrop-blur-sm"
            data-testid="student-sso-loading-backdrop"
            aria-busy="true"
            aria-live="polite"
          >
            <Loader2 className="size-8 animate-spin text-muted-foreground" aria-hidden />
            <span className="sr-only">Opening student hub</span>
          </div>,
          document.body
        )}
      <ShadowContainer className="flex flex-col items-center gap-6 !rounded-3xl bg-white p-6 md:!p-10">
        <div className="flex flex-col items-center gap-2">
          <div className="flex h-[60px] w-[60px] items-center justify-center rounded-full">
            <img src={checkIcon} alt="Check" />
          </div>
          <h1 className="text-center font-merriweather text-[32px] font-bold text-grey-600">Welcome {firstName}</h1>
        </div>
        <p className="text-center text-lg text-grey-600">
          We've created a student profile for {firstName} {lastName}.
        </p>
        <p className="text-center text-lg text-grey-600 md:w-[80%]">
          The Cluey+ free trial will last for {subscriptionDiscountDuration} days and will end on{' '}
          {moment(subscriptionPaidUntilDate).format('DD MMM YYYY')}.
        </p>
        <p className="text-center text-lg text-grey-600 md:w-[80%]">
          We hope you get enough time to explore and we will send a reminder as the free trial is coming to an end.
        </p>
        <Button
          onClick={onExploreClueyPlusClick}
          size="sm"
          className="flex h-10 w-auto items-center justify-center !border text-sm !font-medium tracking-wide text-grey-400 md:w-[195px] md:text-base"
        >
          Explore Cluey+
        </Button>
        <button
          onClick={onGoToProfileClick}
          className="flex h-10 w-auto items-center justify-center !border-none text-sm !font-medium tracking-wide !text-grey-600 hover:!border-none hover:!bg-transparent hover:!text-grey-600 md:text-base"
        >
          Go to {firstName}'s profile
        </button>
      </ShadowContainer>
    </div>
  );
};

export { AddStudentSuccess };
